import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiService from "../services/api";
import { QueryKeys, StaleTimeConfig } from "../contexts/QueryProvider";

/**
 * Hook para obter estatísticas do dashboard
 */
export const useDashboardStats = (targetDbName, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.DASHBOARD_STATS, targetDbName],
    queryFn: () => apiService.getContatosStats(targetDbName),
    enabled: !!targetDbName,
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

/**
 * Hook para obter interações do dashboard
 */
export const useDashboardInteractions = (month = null, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.DASHBOARD_INTERACTIONS, month],
    queryFn: () => apiService.getDashboardInteractions(month),
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

/**
 * Hook para obter total de interações
 */
export const useTotalInteractions = (targetDbName, month = null, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.TOTAL_INTERACTIONS, targetDbName, month],
    queryFn: () => apiService.getInteractions(targetDbName, month ? { month } : undefined),
    enabled: !!targetDbName,
    staleTime: StaleTimeConfig.FREQUENT,
    ...options,
  });
};

/**
 * Hook para obter intervenções humanas
 */
export const useHumanInterventions = (targetDbName, month = null, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.TOTAL_HUMAN_INTERVENTIONS, targetDbName, month],
    queryFn: () => apiService.getHumanInterventions(targetDbName, month ? { month } : undefined),
    enabled: !!targetDbName,
    staleTime: StaleTimeConfig.FREQUENT,
    ...options,
  });
};

/**
 * Hook para obter estatísticas de documentos
 */
export const useDocumentStats = (targetDbName, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.DOCUMENT_STATS, targetDbName],
    queryFn: () => apiService.getDocumentStats(targetDbName),
    enabled: !!targetDbName,
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

/**
 * Hook para obter lista de usuários
 */
export const useUsers = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.USERS, params],
    queryFn: () => apiService.getUsers(params),
    staleTime: StaleTimeConfig.RARE,
    ...options,
  });
};

/**
 * Hook para obter usuário atual
 */
export const useCurrentUser = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.CURRENT_USER],
    queryFn: () => apiService.getCurrentUser(),
    staleTime: StaleTimeConfig.RARE,
    ...options,
  });
};

/**
 * Hook para obter clientes do CRM
 */
export const useCRMClients = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.CRM_CLIENTS],
    queryFn: () => apiService.getCRMClients(),
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

/**
 * Hook para obter estatísticas do CRM
 */
export const useCRMStats = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.CRM_STATS],
    queryFn: () => apiService.getCRMStats(),
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

/**
 * Hook para obter contatos do WhatsApp
 */
export const useWhatsAppContacts = (
  page = 1,
  limit = 50,
  search = "",
  options = {},
) => {
  return useQuery({
    queryKey: [QueryKeys.WHATSAPP_CONTACTS, page, limit, search],
    queryFn: () => apiService.getWhatsAppContacts(page, limit, search),
    staleTime: StaleTimeConfig.FREQUENT,
    keepPreviousData: true,
    ...options,
  });
};

/**
 * Hook para obter estatísticas do WhatsApp
 */
export const useWhatsAppStats = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.WHATSAPP_STATS],
    queryFn: () => apiService.getWhatsAppStats(),
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

/**
 * Hook para obter campanha ativa
 */
export const useActiveCampaign = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.ACTIVE_CAMPAIGN],
    queryFn: () => apiService.getActiveCampaign(),
    staleTime: StaleTimeConfig.REALTIME,
    refetchInterval: 5000, // Refetch a cada 5 segundos
    ...options,
  });
};

/**
 * Hook para obter status de campanha
 */
export const useCampaignStatus = (campaignId, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.CAMPAIGN_STATUS, campaignId],
    queryFn: () => apiService.getCampaignStatus(campaignId),
    enabled: !!campaignId,
    staleTime: StaleTimeConfig.REALTIME,
    refetchInterval: (data) => {
      // Parar refetch se campanha completou ou pausou
      if (
        data?.data?.status === "completed" ||
        data?.data?.status === "failed"
      ) {
        return false;
      }
      return 3000; // 3 segundos para campanhas ativas
    },
    ...options,
  });
};

/**
 * Hook para obter campanhas
 */
export const useCampaigns = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.CAMPAIGNS],
    queryFn: () => apiService.getCampaigns(),
    staleTime: StaleTimeConfig.FREQUENT,
    ...options,
  });
};

/**
 * Hook para obter documentos
 */
export const useDocuments = (targetDbName, params = {}, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.DOCUMENTS, targetDbName, params],
    queryFn: () => apiService.getDocuments(targetDbName, params),
    enabled: !!targetDbName,
    staleTime: StaleTimeConfig.FREQUENT,
    ...options,
  });
};

/**
 * Hook para obter notificações
 */
export const useNotifications = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.NOTIFICATIONS, params],
    queryFn: () => apiService.getNotifications(params),
    staleTime: StaleTimeConfig.REALTIME,
    refetchInterval: 30000, // Refetch a cada 30 segundos
    ...options,
  });
};

/**
 * Hook para obter contador de notificações não lidas
 */
export const useUnreadNotifications = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.UNREAD_NOTIFICATIONS],
    queryFn: () => apiService.getUnreadNotificationsCount(),
    staleTime: StaleTimeConfig.FREQUENT,
    refetchInterval: 30000, // Refetch a cada 30 segundos
    ...options,
  });
};

/**
 * Hook para obter configurações de IA
 */
export const useIASettings = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.IA_SETTINGS],
    queryFn: () => apiService.getIASettings(),
    staleTime: StaleTimeConfig.RARE,
    ...options,
  });
};

/**
 * Hook para obter estatísticas de IA
 */
export const useIAStats = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.IA_STATS],
    queryFn: () => apiService.getIAStats(),
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

/**
 * Hook para obter contatos
 */
export const useContatos = (targetDbName, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.CONTATOS, targetDbName],
    queryFn: () => apiService.getContatos(targetDbName),
    enabled: !!targetDbName,
    staleTime: StaleTimeConfig.FREQUENT,
    ...options,
  });
};

/**
 * Hook para obter estatísticas de contatos
 */
export const useContatosStats = (targetDbName, options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.CONTATOS_STATS, targetDbName],
    queryFn: () => apiService.getContatosStats(targetDbName),
    enabled: !!targetDbName,
    staleTime: StaleTimeConfig.DEFAULT,
    ...options,
  });
};

// ========== MUTATIONS ==========

/**
 * Hook para criar usuário
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData) => apiService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.USERS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_STATS] });
    },
  });
};

/**
 * Hook para atualizar usuário
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userData }) => apiService.updateUser(id, userData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.USERS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.USER, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CURRENT_USER] });
    },
  });
};

/**
 * Hook para deletar usuário
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId) => apiService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.USERS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_STATS] });
    },
  });
};

/**
 * Hook para marcar notificação como lida
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId) =>
      apiService.markNotificationAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.NOTIFICATIONS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.UNREAD_NOTIFICATIONS],
      });
    },
  });
};

/**
 * Hook para marcar todas notificações como lidas
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiService.markAllNotificationsAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.NOTIFICATIONS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.UNREAD_NOTIFICATIONS],
      });
    },
  });
};

/**
 * Hook para upload de documento
 */
export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, targetDbName }) =>
      apiService.uploadDocument(file, targetDbName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DOCUMENTS, variables.targetDbName],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DOCUMENT_STATS, variables.targetDbName],
      });
    },
  });
};

/**
 * Hook para deletar documento
 */
export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, targetDbName }) =>
      apiService.deleteDocument(documentId, targetDbName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DOCUMENTS, variables.targetDbName],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DOCUMENT_STATS, variables.targetDbName],
      });
    },
  });
};

/**
 * Hook para enviar mensagem WhatsApp
 */
export const useSendWhatsAppMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageData) => apiService.sendWhatsAppMessage(messageData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WHATSAPP_MESSAGES],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WHATSAPP_CONVERSATIONS],
      });
    },
  });
};

/**
 * Hook para criar cliente CRM
 */
export const useCreateCRMClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientData) => apiService.createCRMClient(clientData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CRM_CLIENTS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CRM_STATS] });
    },
  });
};

/**
 * Hook para atualizar cliente CRM
 */
export const useUpdateCRMClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, clientData }) =>
      apiService.updateCRMClient(id, clientData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CRM_CLIENTS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.CRM_CLIENT, variables.id],
      });
    },
  });
};

/**
 * Hook para deletar cliente CRM
 */
export const useDeleteCRMClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId) => apiService.deleteCRMClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CRM_CLIENTS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CRM_STATS] });
    },
  });
};

/**
 * Hook para toggle de campanha (start/pause)
 */
export const useToggleCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, action }) =>
      apiService.toggleCampaign(campaignId, action),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CAMPAIGNS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.CAMPAIGN, variables.campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.CAMPAIGN_STATUS, variables.campaignId],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ACTIVE_CAMPAIGN] });
    },
  });
};

/**
 * Hook para parar campanha
 */
export const useStopCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId) => apiService.stopCampaign(campaignId),
    onSuccess: (_, campaignId) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CAMPAIGNS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.CAMPAIGN, campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.CAMPAIGN_STATUS, campaignId],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ACTIVE_CAMPAIGN] });
    },
  });
};

const hooks = {
  useDashboardStats,
  useDashboardInteractions,
  useTotalInteractions,
  useHumanInterventions,
  useDocumentStats,
  useUsers,
  useCurrentUser,
  useCRMClients,
  useCRMStats,
  useWhatsAppContacts,
  useWhatsAppStats,
  useActiveCampaign,
  useCampaignStatus,
  useCampaigns,
  useDocuments,
  useNotifications,
  useUnreadNotifications,
  useIASettings,
  useIAStats,
  useContatos,
  useContatosStats,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useUploadDocument,
  useDeleteDocument,
  useSendWhatsAppMessage,
  useCreateCRMClient,
  useUpdateCRMClient,
  useDeleteCRMClient,
  useToggleCampaign,
  useStopCampaign,
};

export default hooks;
